import * as angular from "angular";
import "./entities.less";
import template from "./entities.html";
import cloneDeep from "clone-deep";
import {
	filter,
	some,
	findIndex
} from "lodash";
import {
	FuzzySearcher,
	FUZZY_OPTIONS
} from "../../utils/fuzzy-search";
import { pluralize } from "humanize-plus";
import { ENTITY_TRANSLATION_KEYS } from "../../services/i18nService";
import {
	anyOf, 
	globs 
} from "../../services/globs";
import { Pattern } from "../../services/security/actionScopes";
import {
	ENTITIES_SCREEN,
	FORM_ENTITIES
} from "routes.js";
import { isTypeWritePermittedScope } from "../../services/security/commonScopes";
import { ENTITY_RELATIONSHIP_KINDS } from "../../constants";
import { showEntityModal } from "./prompts";

const EDIT_MODE = "edit";

export function controller(
	appState,
	api,
	loadingService,
	tableHelper,
	$uibModal,
	$logger,
	securityService,
	$scope,
	$stateParams,
	$state,
	$transitions,
	$location,
	entityManager
) {
	const ctrl = this;

	ctrl.promiseTracker = loadingService.getTracker();

	ctrl.$onInit = onInit;

	ctrl._uibModal = $uibModal;
	ctrl.onCollapse = onCollapse;
	ctrl.tableWidthCss = "col-sm-9";
	ctrl.ENTITY_TRANSLATION_KEYS = ENTITY_TRANSLATION_KEYS;

	ctrl.searcher = new FuzzySearcher([], FUZZY_OPTIONS.ENTITY);

	ctrl.table = {
		data: [],
		actions: {},
		sort: {
			predicate: "Name",
			direction: "asc"
		},
		filter: () => {
			tableHelper.filter(ctrl.table, ctrl.searcher, { defaultSortBy: "Name" }); 
			ctrl.paginatorOptions = {
				...ctrl.paginatorOptions,
				totalItems: ctrl.table.data.length
			};
		},
		reset: () => reset(),
		filterBy: null
	};

	ctrl.paginatorOptions = {};
	ctrl.ENTITY_TRANSLATION_KEYS = ENTITY_TRANSLATION_KEYS;

	function onInit() {
		ctrl.form = appState.forms.selected;
		ctrl.permissions = cloneDeep(appState.permissions.Administration.Entity);
		ctrl.permissions.Type = filter(ctrl.permissions.Type, t => !t.IsDisabled);

		const formEntityTypeCodes = new Set(ctrl.form.Fields.flatMap(field => [
			field.EntityQueryConfiguration?.Primary?.Type?.Code,
			field.EntityQueryConfiguration?.Friendly?.Type?.Code
		]));
		const allEntityTypes = ctrl.form.Agency.Types.filter(type => formEntityTypeCodes.has(type.Code));

		const shownEntityTypes = allEntityTypes.filter(t => {
			if(!t.Relationships.Items?.length) {
				return true;
			}

			const hasNoTargets = t.Relationships.Items.some(i => i.Target === undefined);
			if(hasNoTargets) {
				return true;
			}

			const hasNoKinds = t.Relationships.Items.some(i => i.Kind === undefined);
			if(hasNoKinds) {
				return true;
			}

			const hasReverseRelationship = t.Relationships.Items.some(i => i.Kind.Code === ENTITY_RELATIONSHIP_KINDS.Reverse);

			return !hasReverseRelationship;
		}).sort((a, b) => a.Name.localeCompare(b.Name, undefined, { sensitivity: "base" }));
		
		ctrl.allowedEntityTypes = shownEntityTypes.filter(t => securityService.isActionScopeAllowed(isTypeWritePermittedScope(t.Code)));

		const primaryFriendlyTypeCode = entityManager.primaryEntityQueryConfiguration?.Friendly?.Type?.Code;
		const defaultType = ctrl.allowedEntityTypes.find(t => t.Code === primaryFriendlyTypeCode) || ctrl.allowedEntityTypes[0];

		// Preserve an allowed URL type, otherwise use the primary entity's Friendly type
		const initialType = ctrl.allowedEntityTypes.find(t => t.Code === $stateParams.type) || defaultType;

		// Load initial data and handle deep link
		handleStateChange({
			type: initialType, 
			search: $stateParams.search, 
			id: $stateParams.id, 
			action: $stateParams.action,
			mode: $stateParams.mode
		});

		// Watch for entity type changes from user interaction
		$scope.$watch(() => ctrl.selected, (newType, oldType) => {
			if(!newType || !oldType || newType === oldType) {
				return;
			}
			const entityType = ctrl.allowedEntityTypes.find(t => t.Code === newType);
			if(entityType) {
				handleStateChange({ type: entityType });
			}
		});

		// Watch for search filter changes to sync URL and filter table
		$scope.$watch(() => ctrl.table.filterBy, (newValue, oldValue) => {
			if(newValue !== oldValue) {
				$location.search("search", newValue || null).replace();
				ctrl.table.filter();
			}
		});

		// Watch successful transitions so non-URL state params are available
		const deregisterStateChange = $transitions.onSuccess({ to: ENTITIES_SCREEN }, transition => {
			const routeParams = transition.params("to");
			const typeCode = routeParams.type;
			const newType = typeCode && typeCode !== ctrl.selected
				? ctrl.allowedEntityTypes.find(t => t.Code === typeCode)
				: null;
			
			handleStateChange({
				type: newType, 
				search: routeParams.search,
				id: routeParams.id,
				action: routeParams.action,
				mode: routeParams.mode
			});
		});
		$scope.$on("$destroy", deregisterStateChange);
	}

	/**
	 * Centralized handler for state changes - evaluates type, search, and modal state
	 */
	async function handleStateChange({
		type, 
		search, 
		id, 
		action,
		mode
	} = {}) {
		// Handle type change - reload entities
		if(type) {
			await loadEntities(type);
		}

		// Handle search change - sync filter and update table
		if(search !== ctrl.table.filterBy) {
			ctrl.table.filterBy = search || null;
		}

		// Handle modal state (id/action)
		handleModalState({ id, action, mode });
	}

	/**
	 * Handle modal state based on URL params - opens entity or add modal
	 */
	let currentModalId = null;

	function handleModalState({
		id,
		action,
		mode
	} = {}) {
		// Skip if a modal is already open — URL changes from child modals or tab
		// navigation within the modal should not trigger a new top-level modal
		if(currentModalId !== null) {
			return;
		}
		if(!ctrl.selected) {
			if(id || action) {
				resetModal();
			}
			return;
		}
		// action and id are mutually exclusive - id takes precedence
		if(id) {
			if($location.search().action) {
				$location.search("action", null).replace();
			}
			const entity = ctrl.searcher.collection.find(e => e.Id == id);
			if(entity) {
				const initialMode = mode === EDIT_MODE
					? EDIT_MODE
					: undefined;
				openModal(entity, initialMode);
			}
			else {
				resetModal();
			}
		}
		else if(action === "add") {
			openModal();
		}
	}

	async function loadEntities(entityType) {
		//if entity type has identifiers, include them in the query if the user has permission for it
		const hasIdentifiers = 
			entityType.Identifiers !== undefined && 
			entityType.Identifiers?.Code != "UNSUPPORTED" && 
			(entityType.Identifiers?.Items?.length ?? 0) > 0
		;
		const allowedIdentifiers = [];

		if(hasIdentifiers) {
			const identifierActions = globs.entityTypes
				.byType(entityType.Code)
				.identifiers
			;

			for(const identifier of entityType.Identifiers.Items) {
				if(securityService.isActionScopeAllowed(new Pattern(identifierActions.byCode(identifier.Item.Code).read))) {
					allowedIdentifiers.push(identifier.Item.Code);
				}
			}
		}
		const query = [{
			Type: { Code: entityType.Code }, Include: { Identifiers: allowedIdentifiers }
		}];

		const additionalHeaders = { "X-Vesl-Features-Advanced-Entity-Query": true };
		ctrl.selected = entityType.Code;
		ctrl.entityTypeName = entityType.Name;
		ctrl.pluralName = pluralize(2, entityType.Name ?? entityType.Code);
		ctrl.createAuthorizeAction = globs.entityTypes.byType(entityType.Code).write;
		const anyEntitySubPath = anyOf(globs.entityTypes.byType(entityType.Code).self);

		if($location.search().type !== entityType.Code) {
			$location.search("type", entityType.Code).replace();
		}

		ctrl.canModify = securityService.isPatternAllowed(anyEntitySubPath);

		try {
			const promise = api.post(FORM_ENTITIES(ctrl.form.Id), query, { headers: additionalHeaders });
			const results = await loadingService.watch(promise);

			setTableData(results[0].Entities || []);
			ctrl.table.filter();

			$scope.$apply();
		}
		catch(err) {
			$logger.exception(err, "There was an error attempting to load entities.");

			setTableData([]);
		}
	}

	function resetModal() {
		$state.go(ENTITIES_SCREEN, {
			id: null,
			action: null
		}, { location: "replace" });
	}

	function navigateToEntity(entity, mode) {
		$state.go(ENTITIES_SCREEN, {
			action: null,
			id: String(entity.Id),
			mode
		}, { location: "replace" });
	}

	async function openModal(entity, mode) {
		currentModalId = $location.search().id || "add";
		let createdEntity;
		try {
			const updatedEntity = await showEntityModal($uibModal, {
				entity,
				typeConfig: ctrl.form.Agency.Types.find(t => t.Code == ctrl.selected),
				permissions: ctrl.permissions,
				mode,
				syncUrl: true
			});

			if(entity === undefined && updatedEntity !== undefined) {
				createdEntity = updatedEntity;
				setTableData([...ctrl.searcher.collection, updatedEntity]);
				ctrl.table.filter();
			}
			else if(updatedEntity) {
				Object.assign(entity, updatedEntity);

				const entityFields = ctrl.form.Fields.filter((field) => {
					return field.Type.Code === "ENTITY" || field.Type.Code === "ENTITYCONTACT";
				});

				const applicableEntityFields = entityFields.filter((field) => {
					return some(field.Data, (data) => {
						return data.Id === updatedEntity.Id;
					});
				});

				for(let i = 0; i < applicableEntityFields.length; i++) {
					const entityIndex = findIndex(applicableEntityFields[i].Data, (data) => data.Id === updatedEntity.Id);

					applicableEntityFields[i].Data[entityIndex].Contacts = updatedEntity.Contacts ?? applicableEntityFields[i].Data[entityIndex].Contacts;
					applicableEntityFields[i].Data[entityIndex].Name = updatedEntity.Name ?? applicableEntityFields[i].Data[entityIndex].Name;
					applicableEntityFields[i].Data[entityIndex].Code = updatedEntity.Code ?? applicableEntityFields[i].Data[entityIndex].Code;
				}
			}
		}
		finally {
			currentModalId = null;
			if(createdEntity?.Id) {
				navigateToEntity(createdEntity, EDIT_MODE);
			}
			else {
				resetModal();
			}
		}
	}

	function setTableData(entities) {
		ctrl.table.data = entities;

		const typeConfig = ctrl.form.Agency.Types.find(t => t.Code == ctrl.selected);
		ctrl.table.hasExternalKeys = typeConfig.IsExternalKeySystemManaged !== true &&
			(entities.some(e => e.ExternalKey !== undefined) || typeConfig.ExternalKey?.IsSupported || typeConfig.ExternalKey?.IsRequired);
		ctrl.table.hideCodeColumn = typeConfig.IsCodeSystemManaged === true;
		
		ctrl.searcher.setCollection(entities);
		ctrl.paginatorOptions = {
			...ctrl.paginatorOptions, 
			totalItems: ctrl.table.data.length
		};
	}


	function reset() {
		ctrl.table.filterBy = null;
		ctrl.table.filter();
	}

	function onCollapse() {
		ctrl.tableWidthCss = ctrl.isCollapsed ? "col-sm-12" : "col-sm-9";
	}
}

export default angular
	.module("vesl.screens.entities", [])
	.component("entities", {
		template,
		controller,
		bindings: {
			form: "<"
		}
	}).name;
